import React, { useState, useEffect } from 'react';
import { 
  PlusIcon,
  PencilIcon,
  PauseIcon,
  PlayIcon,
  EyeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { decryptData } from '../../utils/storageCrypto';

const BudgetDashboard = () => {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState(null);

  useEffect(() => {
    fetchBudgets();
  }, []);

  const fetchBudgets = async () => {
    const encryptedToken = sessionStorage.getItem('token');
        const token = decryptData(encryptedToken);
    try {
      const response = await fetch('/api/buildcoins/budgets', {
        headers: {
           'x-token': token,
           'content-type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setBudgets(data.data);
      }
    } catch (error) {
      console.error('Error fetching budgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditBudget = (budget) => {
    setSelectedBudget(budget);
    setShowCreateModal(true);
  };

  const handlePauseBudget = async (budgetId, currentStatus) => {
    const encryptedToken = sessionStorage.getItem('token');
        const token = decryptData(encryptedToken);
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      const response = await fetch(`/api/buildcoins/budgets/${budgetId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        fetchBudgets(); // Refresh the list
      }
    } catch (error) {
      console.error('Error updating budget status:', error);
    }
  };

  const handleViewDetails = (budget) => {
    setSelectedBudget(budget);
    setShowDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Budget Management</h1>
          <p className="text-gray-600">Manage BuildCoins budgets and allocations</p>
        </div>
        <button
          onClick={() => {
            setSelectedBudget(null);
            setShowCreateModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Create Budget
        </button>
      </div>

      {/* Budget Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {budgets.map((budget) => (
          <div key={budget._id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{budget.name}</h3>
                <p className="text-sm text-gray-500">{budget.description}</p>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                budget.status === 'active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {budget.status}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Budget:</span>
                <span className="text-sm font-medium">{budget.total_amount?.toLocaleString()} coins</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Used:</span>
                <span className="text-sm font-medium">{budget.used_amount?.toLocaleString()} coins</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Remaining:</span>
                <span className="text-sm font-medium">{(budget.total_amount - budget.used_amount)?.toLocaleString()} coins</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div 
                className="bg-blue-600 h-2 rounded-full" 
                style={{ width: `${Math.min((budget.used_amount / budget.total_amount) * 100, 100)}%` }}
              ></div>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center">
              <div className="flex space-x-2">
                <button
                  onClick={() => handleViewDetails(budget)}
                  className="text-blue-600 hover:text-blue-900"
                  title="View Details"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleEditBudget(budget)}
                  className="text-gray-600 hover:text-gray-900"
                  title="Edit"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePauseBudget(budget._id, budget.status)}
                  className="text-yellow-600 hover:text-yellow-900"
                  title={budget.status === 'active' ? 'Pause' : 'Resume'}
                >
                  {budget.status === 'active' ? 
                    <PauseIcon className="w-4 h-4" /> : 
                    <PlayIcon className="w-4 h-4" />
                  }
                </button>
              </div>
              <span className="text-xs text-gray-500">
                {new Date(budget.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>

      {budgets.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No budgets found. Create your first budget to get started.</p>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateBudgetModal
          budget={selectedBudget}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedBudget(null);
          }}
          onSave={() => {
            fetchBudgets();
            setShowCreateModal(false);
            setSelectedBudget(null);
          }}
        />
      )}

      {showDetailsModal && selectedBudget && (
        <BudgetDetailsModal
          budget={selectedBudget}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedBudget(null);
          }}
        />
      )}
    </div>
  );
};

// Create Budget Modal Component
const CreateBudgetModal = ({ budget, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: budget?.name || '',
    description: budget?.description || '',
    total_amount: budget?.total_amount || '',
    category: budget?.category || 'general',
    start_date: budget?.start_date ? budget.start_date.split('T')[0] : '',
    end_date: budget?.end_date ? budget.end_date.split('T')[0] : '',
    auto_renewal: budget?.auto_renewal || false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = budget ? `/api/buildcoins/budgets/${budget._id}` : '/api/buildcoins/budgets';
      const method = budget ? 'PUT' : 'POST';
      const encryptedToken = sessionStorage.getItem('token');
          const token = decryptData(encryptedToken);
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving budget:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {budget ? 'Edit Budget' : 'Create Budget'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Budget Name
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Amount (coins)
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.total_amount}
                onChange={(e) => setFormData({ ...formData, total_amount: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="general">General</option>
                <option value="marketing">Marketing</option>
                <option value="customer_retention">Customer Retention</option>
                <option value="seasonal">Seasonal</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  required
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  required
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="auto_renewal"
                checked={formData.auto_renewal}
                onChange={(e) => setFormData({ ...formData, auto_renewal: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="auto_renewal" className="ml-2 block text-sm text-gray-900">
                Auto-renewal
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (budget ? 'Update Budget' : 'Create Budget')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Budget Details Modal Component
const BudgetDetailsModal = ({ budget, onClose }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBudgetTransactions();
  }, [budget._id]);

  const fetchBudgetTransactions = async () => {
    const encryptedToken = sessionStorage.getItem('token');
        const token = decryptData(encryptedToken);
    try {
      const response = await fetch(`/api/buildcoins/budgets/${budget._id}/transactions`, {
        headers: {
          'x-token': token
        }
      });
      const data = await response.json();
      if (data.success) {
        setTransactions(data.data);
      }
    } catch (error) {
      console.error('Error fetching budget transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const usagePercentage = (budget.used_amount / budget.total_amount) * 100;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Budget Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Budget Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">Budget Information</h3>
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm text-gray-500">Name</dt>
                  <dd className="text-sm font-medium text-gray-900">{budget.name}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Description</dt>
                  <dd className="text-sm font-medium text-gray-900">{budget.description || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Category</dt>
                  <dd className="text-sm font-medium text-gray-900 capitalize">{budget.category}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Status</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      budget.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {budget.status}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Period</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {new Date(budget.start_date).toLocaleDateString()} - {new Date(budget.end_date).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">Usage Statistics</h3>
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm text-gray-500">Total Budget</dt>
                  <dd className="text-sm font-medium text-gray-900">{budget.total_amount?.toLocaleString()} coins</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Used Amount</dt>
                  <dd className="text-sm font-medium text-gray-900">{budget.used_amount?.toLocaleString()} coins</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Remaining</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {(budget.total_amount - budget.used_amount)?.toLocaleString()} coins
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Usage Percentage</dt>
                  <dd className="text-sm font-medium text-gray-900">{usagePercentage.toFixed(1)}%</dd>
                </div>
              </dl>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>{usagePercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      usagePercentage > 90 ? 'bg-red-600' : 
                      usagePercentage > 75 ? 'bg-yellow-600' : 'bg-green-600'
                    }`}
                    style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Transactions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Transactions</h3>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : transactions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No transactions found for this budget.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaction ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map((transaction) => (
                      <tr key={transaction._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {transaction.transaction_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {transaction.consumer_info?.customer_name || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {transaction.consumer_info?.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.coins_amount?.toLocaleString()} coins
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                          {transaction.transaction_category?.replace('_', ' ')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(transaction.transaction_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            transaction.status === 'approved' ? 'bg-green-100 text-green-800' :
                            transaction.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {transaction.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetDashboard;
